import * as React from 'react';
import { findIndex } from '../../utils/FindIndex';
import { mergeEventHandlers } from '../../utils/ReactHelpers';
import { findIndexOfItemWithValue, findSelectableItemIndex, isSelectableItem, ListItem } from '../List/ListItemUtils';
import { OverrideListProps, SelectDropdown, SelectDropdownProps } from './SelectDropdown';

export type SingleSelectDropdownProps = SelectDropdownProps<string>;

/**
 * `SingleSelectDropdown` implements Tableau's version of an HTML `<select>`.
 * It is implemented as a `DropdownButton` with a parameterized `List` popup.
 * The caller can provide a `List`, `FilteringList`, `LocallyFilteringList`, or any other component which satisfies the `ListProps` interface.
 *
 * By default, closes the popup upon selection change — unless the `onOpen` prop is provided. In that case, the caller must close the dropdown
 * manually when appropriate. To do so, save a ref to the dropdown and call `toggle(false)` on it.
 */
export class SingleSelectDropdown extends SelectDropdown<string> {
  private typeAheadQuery: string = '';
  private typeAheadTimeoutHandle: number = 0;

  private keyDownHandlers: { [key: string]: (items: ListItem[]) => number } = Object.freeze({
    'Home': items => findSelectableItemIndex(items, -1, +1),
    'End': items => findSelectableItemIndex(items, items.length, -1),
    'ArrowUp': items => findSelectableItemIndex(items, findIndexOfItemWithValue(items, this.props.value), -1),
    'ArrowDown': items => findSelectableItemIndex(items, findIndexOfItemWithValue(items, this.props.value), +1),
  });
  private handleKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = mergeEventHandlers(e => {
    if (this.props.disabled || !this.keyDownHandlers[e.key]) { return; }

    const { items } = this.props.list.props;
    const newSelectedItemIndex = this.keyDownHandlers[e.key](items);
    e.preventDefault();
    e.stopPropagation();

    if (!items[newSelectedItemIndex]) {
      this.toggle(true); // if we can't find an item, open the dropdown instead, the items are probably being lazily loaded
      return;
    }

    const newValue = items[newSelectedItemIndex].value!;
    if (newValue !== this.props.value) {
      this.handleSelect(newValue);
    }
  }, e => this.props.onKeyDown?.(e));

  private handleKeyPress: React.KeyboardEventHandler<HTMLButtonElement> = mergeEventHandlers(e => {
    if (this.props.disabled || e.key === ' ' || e.key === 'Enter' || e.altKey || e.ctrlKey || e.metaKey) { return; }

    window.clearTimeout(this.typeAheadTimeoutHandle);
    this.typeAheadTimeoutHandle = window.setTimeout(() => this.typeAheadQuery = '', 500);
    this.typeAheadQuery += e.key;

    const { items } = this.props.list.props;
    const currentIndex = findIndex(items, item => item.value === this.props.value);

    function lookFor(query: string): number {
      // single-character queries start after the current item, but multiple-character queries start on the current item
      const startAt = currentIndex + (query.length > 1 ? 0 : 1);
      return findIndex(items, item => isSelectableItem(item) && item.value!.toLowerCase().indexOf(query.toLowerCase()) === 0, startAt);
    }

    let nextIndex = lookFor(this.typeAheadQuery);
    if (nextIndex < 0) {
      nextIndex = lookFor(e.key);
    }

    if (nextIndex >= 0 && currentIndex !== nextIndex) {
      this.handleSelect(items[nextIndex].value!);
      e.preventDefault();
      e.stopPropagation();
    }
  }, e => this.props.onKeyPress?.(e));

  private handleSelect = (value: string): void => {
    this.props.onChange?.(value);
    if (!this.props.onOpen) {
      this.toggle(false);
    }
  }

  protected makeListProps(): OverrideListProps {
    return {
      onSelect: this.handleSelect,
      onSelectAll: undefined!,
      selectOnEnterKey: true,
      selectedValue: this.props.value!,
      selectedValues: undefined!,
    };
  }

  protected renderButtonContent(): React.ReactNode {
    const { children, list, value } = this.props;
    const selectedItem = list.props.items.find(item => item.value === value);

    return children ?? (selectedItem?.content || value || '');
  }

  public render(): React.ReactElement {
    return React.cloneElement(super.render(), {
      onKeyDown: this.handleKeyDown,
      onKeyPress: this.handleKeyPress,
    });
  }
}
